import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ComingSoon from '../pages/ComingSoon';
import Home from '../pages/Home';
import AboutUs from '../pages/AboutUs';
import Founders from '../pages/Founders';
import CadencePromise from '../pages/CadencePromise';
import WhyWeExist from '../pages/WhyWeExist';
import Pricing from '../pages/Pricing';
import Membership from '../pages/Membership';
// CADENCE Compass page — use CompassPreview (same component as /compass-preview)
// so the foil-C + LuxeCompassWatermark + content tabs render identically
// in both the curtain (/articles) and the standalone (/compass-preview) view.
import Articles from '../pages/CompassPreview';

// Base page definitions for curtain navigation
const BASE_CURTAIN_PAGES = [
  { path: '/home', name: 'Home', component: Home },
  { path: '/founders', name: 'Meet the Founders', component: Founders },
  { path: '/about-us', name: 'The CADENCE Mission', component: AboutUs },
  { path: '/cadence-promise', name: 'CADENCE Promise', component: CadencePromise },
  { path: '/why-we-exist', name: 'Why We Exist', component: WhyWeExist },
  { path: '/pricing', name: 'Pricing', component: Pricing },
  { path: '/membership', name: 'Membership', component: Membership },
  { path: '/articles', name: 'CADENCE Compass', component: Articles },
];

// Welcome/Landing page for admin users only
const WELCOME_PAGE = { path: '/welcome', name: 'Landing', component: ComingSoon };

// EKG SVG Path - Realistic jagged heartbeat pattern (PQRST wave)
const EKGPath = () => (
  <svg className="ekg-pulse" viewBox="0 0 200 40" preserveAspectRatio="none">
    {/* Realistic EKG: flat baseline -> P wave -> QRS complex -> T wave -> flat */}
    <path d="M0,20 L15,20 L18,18 L21,20 L25,20 L27,22 L29,4 L31,38 L33,16 L35,20 L40,20 L43,17 L48,23 L52,20 L70,20 L73,18 L76,20 L80,20 L82,22 L84,2 L86,39 L88,14 L90,20 L95,20 L98,17 L103,23 L107,20 L125,20 L128,18 L131,20 L135,20 L137,22 L139,3 L141,38 L143,15 L145,20 L150,20 L153,17 L158,23 L162,20 L200,20" />
  </svg>
);

const CurtainNavigation = () => {
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTouched, setIsTouched] = useState(false);
  // Hide the fixed EKG nav once the footer scrolls into view so it never
  // floats over the footer's contact/legal content (user feedback 2026-06).
  const [footerVisible, setFooterVisible] = useState(false);
  useEffect(() => {
    const footer = document.querySelector('.footer');
    if (!footer) {
      setFooterVisible(false);
      return undefined;
    }
    const obs = new IntersectionObserver(
      ([entry]) => setFooterVisible(entry.isIntersecting),
      { rootMargin: '0px 0px -8% 0px' }
    );
    obs.observe(footer);
    return () => obs.disconnect();
  }, [activeIndex]);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const touchTimeoutRef = useRef(null);

  // Check if user is admin/staff
  const isAdminUser = user && ['admin', 'staff', 'reviewer', 'provider'].includes(user.role);
  
  // Build pages array - include Welcome page for admin users
  const CURTAIN_PAGES = isAdminUser 
    ? [WELCOME_PAGE, ...BASE_CURTAIN_PAGES]
    : BASE_CURTAIN_PAGES;

  // Find current page index from URL
  const getCurrentIndex = useCallback(() => {
    const index = CURTAIN_PAGES.findIndex(p => p.path === location.pathname);
    return index >= 0 ? index : 0;
  }, [location.pathname, CURTAIN_PAGES]);

  // Scroll to specific page - with special handling for circular navigation
  const scrollToPage = useCallback((index, isCircularJump = false) => {
    if (containerRef.current && index >= 0 && index < CURTAIN_PAGES.length) {
      isScrollingRef.current = true;
      const container = containerRef.current;
      const pageWidth = container.clientWidth;
      const targetPosition = index * pageWidth;
      
      // For circular jumps (last→first or first→last), use aggressive instant teleport
      if (isCircularJump) {
        // Add class to completely disable scroll behaviors
        container.classList.add('instant-jump');

        // Force synchronous style application
        container.style.setProperty('scroll-behavior', 'auto', 'important');
        container.style.setProperty('scroll-snap-type', 'none', 'important');
        container.style.setProperty('transition', 'none', 'important');

        // Force reflow
        void container.offsetWidth;

        // Instant teleport
        container.scrollLeft = targetPosition;

        // Cancel any pending debounced scrollend reconciliation that could
        // overwrite our target index mid-teleport (bug 2026-05-11: clicking
        // forward from Compass landed back on Membership / Why-We-Exist
        // because handleScrollEnd ran while isScrollingRef was already false
        // and re-set activeIndex from the intermediate scrollLeft value).
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
          scrollTimeoutRef.current = null;
        }

        // Use longer timeout to ensure the scroll has fully completed before re-enabling
        setTimeout(() => {
          if (containerRef.current) {
            // Verify position and force it again if needed
            containerRef.current.scrollLeft = targetPosition;
          }
        }, 20);

        // Re-enable smooth scrolling AFTER a longer guard so the scroll
        // listener's 100 ms debounce can't race with the teleport.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (containerRef.current) {
              containerRef.current.classList.remove('instant-jump');
              containerRef.current.style.removeProperty('scroll-behavior');
              containerRef.current.style.removeProperty('scroll-snap-type');
              containerRef.current.style.removeProperty('transition');
            }
            // Hold the scrolling lock for 400 ms — covers the worst-case
            // scrollend debounce (100 ms) plus any native snap settle.
            setTimeout(() => {
              isScrollingRef.current = false;
              if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
              }
            }, 400);
          });
        });
      } else {
        // Normal smooth scroll for regular navigation
        container.scrollTo({
          left: targetPosition,
          behavior: 'smooth'
        });
        
        // Reset scrolling flag after animation
        setTimeout(() => {
          isScrollingRef.current = false;
        }, 500);
      }
    }
  }, []);

  // Initialize scroll position based on URL — ONLY ON MOUNT
  // Removing getCurrentIndex from deps prevents this effect from firing on
  // every render, which was teleporting scrollLeft mid-swipe and aborting
  // the user's gesture. URL-driven scrolls happen via the next effect below.
  useEffect(() => {
    const index = getCurrentIndex();
    setActiveIndex(index);
    if (containerRef.current) {
      const pageWidth = containerRef.current.clientWidth;
      containerRef.current.scrollLeft = index * pageWidth;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle URL changes (from menu / footer clicks)
  // BUGFIX 2026-06: footer links that jump MORE THAN ONE page (e.g.
  // "Articles & Insights" → /articles from Home) used a multi-page smooth
  // scroll whose 500ms unlock fired mid-scroll. The scrollend listener then
  // read an intermediate scrollLeft and re-navigated to whatever page was
  // passing by (usually /cadence-promise), so the visitor never reached the
  // target. For any jump of >1 page, use the robust instant-teleport path
  // (same guard used for circular wrap) so we land exactly on the target.
  useEffect(() => {
    const index = getCurrentIndex();
    if (index !== activeIndex) {
      const isFarJump = Math.abs(index - activeIndex) > 1;
      setActiveIndex(index);
      scrollToPage(index, isFarJump);
    }
  }, [location.pathname, getCurrentIndex, activeIndex, scrollToPage]);

  // P0 FIX: Reset scroll to top when page changes (swipe reset)
  useEffect(() => {
    // Find the current page element and scroll it to top
    const currentPage = containerRef.current?.children[activeIndex];
    if (currentPage) {
      currentPage.scrollTop = 0;
    }
  }, [activeIndex]);

  // Handle manual swipe/scroll - only update URL after scroll settles
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScrollEnd = () => {
      // Clear any pending timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Wait for scroll to settle before updating state
      scrollTimeoutRef.current = setTimeout(() => {
        // Don't interfere during programmatic scrolling
        if (isScrollingRef.current) return;
        
        const pageWidth = container.clientWidth;
        const scrollLeft = container.scrollLeft;
        const newIndex = Math.round(scrollLeft / pageWidth);
        const maxIndex = CURTAIN_PAGES.length - 1;
        
        // Normal navigation - clamp to valid range
        const clampedIndex = Math.max(0, Math.min(newIndex, maxIndex));
        
        // Only update if different from current
        if (clampedIndex !== activeIndex) {
          setActiveIndex(clampedIndex);
          navigate(CURTAIN_PAGES[clampedIndex].path, { replace: true });
        }
      }, 100); // Short debounce after scroll stops
    };

    container.addEventListener('scrollend', handleScrollEnd, { passive: true });
    // Fallback for browsers without scrollend
    container.addEventListener('scroll', handleScrollEnd, { passive: true });
    
    return () => {
      container.removeEventListener('scrollend', handleScrollEnd);
      container.removeEventListener('scroll', handleScrollEnd);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [activeIndex, navigate]);

  // P0 FIX: Touch/Swipe gesture handler for circular navigation at edges
  // Detects swipe intent when at first/last page and triggers loop
  // FIXED: Only trigger on predominantly HORIZONTAL swipes, not vertical scrolls
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;
    const SWIPE_THRESHOLD = 80; // Increased minimum swipe distance
    const VERTICAL_THRESHOLD_RATIO = 0.5; // If vertical > 50% of horizontal, it's a scroll

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].clientX;
      touchEndY = e.changedTouches[0].clientY;

      const swipeDistanceX = touchStartX - touchEndX;
      const swipeDistanceY = Math.abs(touchStartY - touchEndY);
      const swipeDistanceXAbs = Math.abs(swipeDistanceX);

      // STRICTER: If ANY significant vertical movement, this is a scroll - ignore it
      if (swipeDistanceY > 30 || swipeDistanceY > swipeDistanceXAbs * VERTICAL_THRESHOLD_RATIO) {
        return; // This is a vertical scroll, don't navigate
      }

      const maxIndex = CURTAIN_PAGES.length - 1;

      // Native CSS scroll-snap already handles 1→2, 2→3, etc. The JS handler
      // here ONLY fills the gap that scroll-snap can't: circular wrap at the
      // edges (first→last and last→first). Without this gate every swipe
      // double-fired (native snap moved 1 page, then JS moved another).
      if (swipeDistanceXAbs <= SWIPE_THRESHOLD) return;

      // At LAST page, swiping LEFT (forward) loops to FIRST.
      if (swipeDistanceX > SWIPE_THRESHOLD && activeIndex === maxIndex) {
        setActiveIndex(0);
        scrollToPage(0, true);
        navigate(CURTAIN_PAGES[0].path, { replace: true });
        return;
      }
      // At FIRST page, swiping RIGHT (backward) loops to LAST.
      if (swipeDistanceX < -SWIPE_THRESHOLD && activeIndex === 0) {
        setActiveIndex(maxIndex);
        scrollToPage(maxIndex, true);
        navigate(CURTAIN_PAGES[maxIndex].path, { replace: true });
      }
      // Otherwise: let native scroll-snap handle the page transition; the
      // scrollend listener will sync activeIndex + URL when it settles.
    };

    // Mouse wheel for desktop circular navigation
    const handleWheel = (e) => {
      const maxIndex = CURTAIN_PAGES.length - 1;
      
      // Only trigger at boundaries and with sufficient delta
      if (Math.abs(e.deltaX) > 30) {
        // Scrolling right (positive deltaX) at last page -> loop to first
        if (e.deltaX > 0 && activeIndex === maxIndex) {
          e.preventDefault();
          setActiveIndex(0);
          scrollToPage(0, true);
          navigate(CURTAIN_PAGES[0].path, { replace: true });
        }
        // Scrolling left (negative deltaX) at first page -> loop to last
        else if (e.deltaX < 0 && activeIndex === 0) {
          e.preventDefault();
          setActiveIndex(maxIndex);
          scrollToPage(maxIndex, true);
          navigate(CURTAIN_PAGES[maxIndex].path, { replace: true });
        }
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('wheel', handleWheel);
    };
  }, [activeIndex, navigate, scrollToPage]);

  // Handle window resize + PWA rotation - keep page aligned
  // iOS PWA fires `orientationchange` before/instead of `resize`, so listen
  // to both. Defer the realignment a frame to let the layout settle first
  // (clientWidth is stale during the rotation transition).
  useEffect(() => {
    const realign = () => {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          const pageWidth = containerRef.current.clientWidth;
          containerRef.current.scrollLeft = activeIndex * pageWidth;
        }
      });
    };

    window.addEventListener('resize', realign);
    window.addEventListener('orientationchange', realign);
    return () => {
      window.removeEventListener('resize', realign);
      window.removeEventListener('orientationchange', realign);
    };
  }, [activeIndex]);

  // P1 FIX: Keyboard navigation with circular loop (Arrow keys)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        // Move to next page, loop to first if at last
        const isAtLastPage = activeIndex >= CURTAIN_PAGES.length - 1;
        const nextIndex = isAtLastPage ? 0 : activeIndex + 1;
        setActiveIndex(nextIndex);
        scrollToPage(nextIndex, isAtLastPage);
        navigate(CURTAIN_PAGES[nextIndex].path, { replace: true });
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        // Move to previous page, loop to last if at first
        const isAtFirstPage = activeIndex <= 0;
        const prevIndex = isAtFirstPage ? CURTAIN_PAGES.length - 1 : activeIndex - 1;
        setActiveIndex(prevIndex);
        scrollToPage(prevIndex, isAtFirstPage);
        navigate(CURTAIN_PAGES[prevIndex].path, { replace: true });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, navigate, scrollToPage]);

  // Calculate progress percentage
  const progressPercent = ((activeIndex + 1) / CURTAIN_PAGES.length) * 100;

  // Navigate to next page with circular looping.
  // Forward wrap always lands on /home (Expert Medical Opinion) so admin
  // users don't get sent back to the gated ComingSoon page that sits at
  // curtain index 0 for them. Non-admins behave identically (index 0 is
  // /home for them, so the lookup just returns 0).
  const goToNextPage = useCallback(() => {
    const isAtLastPage = activeIndex >= CURTAIN_PAGES.length - 1;
    const homeIndex = CURTAIN_PAGES.findIndex((p) => p.path === '/home');
    const wrapTarget = homeIndex >= 0 ? homeIndex : 0;
    const nextIndex = isAtLastPage ? wrapTarget : activeIndex + 1;
    setActiveIndex(nextIndex);
    scrollToPage(nextIndex, isAtLastPage);
    navigate(CURTAIN_PAGES[nextIndex].path, { replace: true });
  }, [activeIndex, navigate, scrollToPage]);

  // Navigate to previous page with circular looping.
  // Backward wrap lands on the last curtain page (Membership) without
  // the ComingSoon detour for admin users.
  const goToPrevPage = useCallback(() => {
    const homeIndex = CURTAIN_PAGES.findIndex((p) => p.path === '/home');
    const isAtFirstPage = activeIndex <= (homeIndex >= 0 ? homeIndex : 0);
    const prevIndex = isAtFirstPage ? CURTAIN_PAGES.length - 1 : activeIndex - 1;
    setActiveIndex(prevIndex);
    scrollToPage(prevIndex, isAtFirstPage);
    navigate(CURTAIN_PAGES[prevIndex].path, { replace: true });
  }, [activeIndex, navigate, scrollToPage]);

  // Handle touch for EKG animation
  const handleEkgTouch = () => {
    setIsTouched(true);
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
    }
    // Keep EKG animated for 2 seconds after touch ends
    touchTimeoutRef.current = setTimeout(() => {
      setIsTouched(false);
    }, 2000);
  };

  return (
    <div className="curtain-navigation" data-testid="curtain-navigation">
      {/* P0 FIX: Left navigation arrow for circular navigation */}
      <button 
        className="curtain-nav-arrow curtain-nav-arrow-left"
        onClick={goToPrevPage}
        aria-label="Previous page"
        data-testid="nav-arrow-left"
      >
        <ChevronLeft size={32} />
      </button>

      {/* P0 FIX: Right navigation arrow for circular navigation */}
      <button 
        className="curtain-nav-arrow curtain-nav-arrow-right"
        onClick={goToNextPage}
        aria-label="Next page"
        data-testid="nav-arrow-right"
      >
        <ChevronRight size={32} />
      </button>

      {/* Horizontal scrolling container */}
      <div 
        ref={containerRef}
        className="curtain-scroll-container"
      >
        {CURTAIN_PAGES.map((page, index) => {
          const PageComponent = page.component;
          return (
            <section 
              key={page.path}
              className="curtain-page"
              data-testid={`curtain-page-${page.name.toLowerCase().replace(' ', '-')}`}
            >
              <div className="curtain-page-content">
                <PageComponent />
              </div>
            </section>
          );
        })}
      </div>
      
      {/* Sleek Gold Line Navigation */}
      <div 
        className={`ekg-navigation ${isTouched ? 'touched' : ''} ${footerVisible ? 'at-footer' : ''}`} 
        data-testid="ekg-navigation"
        onTouchStart={handleEkgTouch}
        onMouseDown={handleEkgTouch}
        onClick={goToNextPage}
      >
        <div className="ekg-line-container">
          {/* Base line */}
          <div className="ekg-line" />
          
          {/* Animated EKG pulse */}
          <EKGPath />
          
          {/* Progress bar */}
          <div 
            className="ekg-progress-bar" 
            style={{ width: `${progressPercent}%` }}
          />
          
          {/* Page markers - invisible touch targets */}
          <div className="ekg-markers">
            {CURTAIN_PAGES.map((page, index) => (
              <button
                key={page.path}
                className={`ekg-marker ${index === activeIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const isFarJump = Math.abs(index - activeIndex) > 1;
                  setActiveIndex(index);
                  scrollToPage(index, isFarJump);
                  navigate(page.path);
                }}
                aria-label={`Go to ${page.name}`}
                title={page.name}
              >
                <span className="ekg-marker-label">{page.name}</span>
              </button>
            ))}
          </div>
        </div>
        {/* Contextual tap hint based on current page */}
        <span className="tap-hint">
          {activeIndex === 1 ? 'This is the pain we wish to fix:' : 'tap for next'}
        </span>
      </div>
    </div>
  );
};

export default CurtainNavigation;
